import { FC } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { Link as CarbonLink } from '@carbon/react';
import { useAtomValue } from 'jotai';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { emptyAllocationManagersLoadableAtom } from './atoms';
import type { EmptyAllocationManager } from './types';
import styles from './page.module.scss';

const TABLE_CONFIG = {
  sorting: true,
  globalSearch: true,
  columnFilters: false,
  columnResizing: true,
  columnReordering: true,
  columnPinning: true,
  columnVisibility: true,
} as const;

const columnHelper = createColumnHelper<EmptyAllocationManager>();

const columns = [
  columnHelper.accessor('wellUWI', {
    header: 'Well UWI',
    size: 140,
    cell: (info) => {
      const value = info.getValue();
      if (!value) return '--';
      return (
        <CarbonLink
          href={`/wells/${value}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {value}
        </CarbonLink>
      );
    },
  }),
  columnHelper.accessor('unitName', {
    header: 'Unit',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      const row = info.row.original;
      if (!value) return '--';
      return (
        <CarbonLink
          href={`/development_areas/${row.unitId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {value}
        </CarbonLink>
      );
    },
  }),
  columnHelper.accessor('allocationMethod', {
    header: 'Allocation Method',
    size: 150,
  }),
  columnHelper.accessor('unitWellCount', {
    header: 'Wells in Unit',
    size: 120,
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    size: 180,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleDateString() : '--';
    },
  }),
  columnHelper.accessor('allocationManagerId', {
    header: 'AM ID',
    size: 80,
  }),
];

export const EmptyAllocationManagersQueryPage: FC = () => {
  const loadable = useAtomValue(emptyAllocationManagersLoadableAtom);

  const isLoading = loadable.state === 'loading';
  const data = loadable.state === 'hasData' ? loadable.data.results : [];
  const stats = loadable.state === 'hasData' ? loadable.data.stats : null;

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Empty Allocation Managers"
        actions={null}
      />

      <div className={styles.sectionHeader}>
        <p>
          Allocation managers with zero line items. Wells routed through these
          AMs silently produce no NRI result during calculation.
        </p>
        <p className={styles.sectionDescription}>
          Expected: Empty &mdash; all allocation managers should have at least
          one line item linking them to a unit allocation tract.
        </p>
      </div>

      {stats && (
        <StatsBar
          stats={[
            {
              label: 'Empty AMs',
              value: stats.totalEmpty,
              tagType: 'red',
            },
            {
              label: 'Affected Wells',
              value: stats.affectedWells,
              tagType: 'magenta',
            },
            {
              label: 'Affected Units',
              value: stats.affectedUnits,
              tagType: 'teal',
            },
          ]}
        />
      )}

      <DataTable
        data={data}
        columns={columns as ColumnDef<EmptyAllocationManager>[]}
        getRowId={(row) => `${row.allocationManagerId}`}
        isLoading={isLoading}
        {...TABLE_CONFIG}
      />
    </>
  );
};
